// The optional call tolerates stream shims that do not implement the full MediaStream surface.
export const getAudioTracks = (stream: Nullable<MediaStream>): MediaStreamTrack[] =>
  stream?.getAudioTracks?.() ?? [];

/**
 * Whether the stream is positively known to be dead: it has audio tracks, and none of them are
 * still `live`. Chrome pins a track to the physical device, so a USB dictaphone being unplugged
 * moves its track to `ended` permanently — replugging neither revives it nor fires an event.
 *
 * Deliberately not `every(ended)`, which is vacuously true for a stream carrying no tracks at
 * all. A trackless stream is indistinguishable from one we cannot introspect, and must not be
 * torn down on suspicion.
 */
export const hasOnlyEndedAudioTracks = (tracks: MediaStreamTrack[]): boolean =>
  tracks.length > 0 && !tracks.some((track) => track.readyState === 'live');

export type StreamVerdict =
  | 'valid'
  | 'not-acquired'
  | 'device-changed'
  | 'device-list-changed'
  | 'stream-dead';

/**
 * Why the held stream can or cannot keep being used. A reason rather than a boolean, so the
 * decision is nameable in a log line and assertable in a test.
 *
 * Only `'valid'` vs everything else drives control flow, so the ordering among the failure
 * reasons reports the first thing noticed rather than a priority.
 */
export const assessStream = ({
  isRecorderInitialized,
  acquiredDeviceId,
  audioInputDeviceId,
  acquiredDeviceListKey,
  audioInputDeviceListKey,
  tracks,
}: {
  isRecorderInitialized: boolean;
  acquiredDeviceId: string | null;
  audioInputDeviceId: string;
  acquiredDeviceListKey: string | null;
  audioInputDeviceListKey: string | null;
  tracks: MediaStreamTrack[];
}): StreamVerdict => {
  if (!isRecorderInitialized) return 'not-acquired';
  if (acquiredDeviceId !== audioInputDeviceId) return 'device-changed';

  // A null key on either side means the device list was not known at that point. The stream is
  // routinely acquired before enumerateDevices resolves, and "not known yet" must not read as
  // "changed" — that would re-acquire once on every page load.
  const isSameDeviceList =
    acquiredDeviceListKey == null ||
    audioInputDeviceListKey == null ||
    acquiredDeviceListKey === audioInputDeviceListKey;
  if (!isSameDeviceList) return 'device-list-changed';

  if (hasOnlyEndedAudioTracks(tracks)) return 'stream-dead';

  return 'valid';
};
